type JsonRecord = Record<string, unknown>;

export interface ChatUser {
  id: string;
  fullName: string;
  email: string;
  avatar?: string;
  avatarUrl?: string;
}

export interface ChatAttachment {
  name: string;
  url: string;
  type: string;
  size: number;
  [key: string]: unknown;
}

export interface ChatReaction {
  emoji: string;
  users: string[];
}

export interface ChatMessage {
  id: string;
  clientId?: string;
  conversation: string;
  conversationId: string;
  sender: ChatUser | null;
  type: string;
  content: string;
  attachments: ChatAttachment[];
  reactions: ChatReaction[];
  isEdited: boolean;
  isDeleted: boolean;
  replyTo?: ChatMessage;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatConversation {
  id: string;
  type: 'private' | 'group';
  name?: string;
  avatar?: string;
  avatarUrl?: string;
  participants: ChatUser[];
  lastMessage?: ChatMessage;
  lastMessageAt?: string;
  unreadCount: number;
  createdAt?: string;
}

export interface ChatParticipant extends ChatUser {
  role: 'owner' | 'admin' | 'member' | string;
  lastReadAt?: string;
  joinedAt: string;
}

export interface MessageReadReceipt extends ChatUser {
  readAt: string;
}

export interface ChatInfoAttachment extends ChatAttachment {
  messageId: string;
  senderId?: string;
  senderName: string;
  sentAt: string;
}

function record(value: unknown): JsonRecord {
  return value && typeof value === 'object' ? value as JsonRecord : {};
}

function string(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function normalizeChatUser(value: unknown): ChatUser {
  const input = record(value);
  const id = string(input.id);
  const avatarUrl = optionalString(input.avatarUrl ?? input.avatar ?? input.image);
  return {
    ...input,
    id,
    fullName: string(input.fullName ?? input.name),
    email: string(input.email),
    avatar: avatarUrl,
    avatarUrl,
  } as ChatUser;
}

export function normalizeChatMessage(value: unknown): ChatMessage {
  const input = record(value);
  const id = string(input.id);
  const conversationId = string(input.conversationId ?? input.conversation);
  const attachments = Array.isArray(input.attachments)
    ? input.attachments.filter(item => item && typeof item === 'object') as ChatAttachment[]
    : [];
  const reactions = Array.isArray(input.reactions)
    ? input.reactions.map(reactionValue => {
        const reaction = record(reactionValue);
        return {
          emoji: string(reaction.emoji),
          users: Array.isArray(reaction.users) ? reaction.users.map(user => string(user)).filter(Boolean) : [],
        };
      }).filter(reaction => reaction.emoji.length > 0)
    : [];
  const sender = input.sender ? normalizeChatUser(input.sender) : null;
  const replyTo = input.replyTo ? normalizeChatMessage(input.replyTo) : undefined;
  const createdAt = string(input.createdAt, new Date(0).toISOString());
  return {
    ...input,
    id,
    clientId: optionalString(input.clientId),
    conversation: conversationId,
    conversationId,
    sender,
    type: string(input.type, 'text'),
    content: string(input.content),
    attachments,
    reactions,
    isEdited: input.isEdited === true,
    isDeleted: input.isDeleted === true,
    replyTo,
    deletedAt: optionalString(input.deletedAt),
    createdAt,
    updatedAt: string(input.updatedAt, createdAt),
  } as ChatMessage;
}

export function normalizeChatConversation(value: unknown): ChatConversation {
  const input = record(value);
  const id = string(input.id);
  const avatarUrl = optionalString(input.avatarUrl ?? input.avatar);
  return {
    ...input,
    id,
    type: input.type === 'group' ? 'group' : 'private',
    name: optionalString(input.name),
    avatar: avatarUrl,
    avatarUrl,
    participants: Array.isArray(input.participants) ? input.participants.map(normalizeChatUser) : [],
    lastMessage: input.lastMessage ? normalizeChatMessage(input.lastMessage) : undefined,
    lastMessageAt: optionalString(input.lastMessageAt),
    unreadCount: typeof input.unreadCount === 'number' ? input.unreadCount : 0,
    createdAt: optionalString(input.createdAt),
  } as ChatConversation;
}

export function normalizeChatParticipant(value: unknown): ChatParticipant {
  const input = record(value);
  return {
    ...normalizeChatUser(input),
    role: string(input.role, 'member'),
    lastReadAt: optionalString(input.lastReadAt),
    joinedAt: string(input.joinedAt),
  };
}

export function normalizeMessageReadReceipt(value: unknown): MessageReadReceipt {
  const input = record(value);
  return {
    ...normalizeChatUser(input),
    readAt: string(input.readAt),
  };
}

export function normalizeChatInfoAttachment(value: unknown): ChatInfoAttachment {
  const input = record(value);
  return {
    ...input,
    name: string(input.name),
    url: string(input.url),
    type: string(input.type),
    size: typeof input.size === 'number' ? input.size : 0,
    messageId: string(input.messageId),
    senderId: optionalString(input.senderId),
    senderName: string(input.senderName, 'Unknown'),
    sentAt: string(input.sentAt),
  } as ChatInfoAttachment;
}

export function normalizeChatRealtimePayload(eventType: string, value: unknown): unknown {
  const input = record(value);
  if (eventType === 'message.created') return normalizeChatMessage(value);
  if (eventType === 'message.updated') {
    return { ...input, message: input.message ? normalizeChatMessage(input.message) : undefined };
  }
  if (eventType === 'conversation.updated') {
    return { ...input, lastMessage: input.lastMessage ? normalizeChatMessage(input.lastMessage) : undefined };
  }
  if (eventType.startsWith('conversation.') && input.conversation) {
    return { ...input, conversation: normalizeChatConversation(input.conversation) };
  }
  if (eventType.startsWith('typing.') || eventType.startsWith('presence.')) {
    const avatar = optionalString(input.avatarUrl ?? input.avatar);
    return { ...input, avatar, avatarUrl: avatar };
  }
  return value;
}
